<template>
    <Spinner v-if="displaySpinner" />
    <div class="title-and-controls">
        <div class="title-section">
            <div class="logo-container" :class="{ 'editing': isEditing }">
                <img
                    v-if="logoDataUrl"
                    :src="logoDataUrl"
                    class="stek-logo"
                    alt="Stek Logo"
                    @error="handleLogoError"
                />
                <div
                    v-if="isEditing"
                    class="logo-edit-overlay"
                    @click="triggerFileInput"
                    role="button"
                    tabindex="0"
                    @keydown.enter="triggerFileInput"
                    aria-label="Change logo"
                >
                    <i class="pi pi-pencil"></i>
                    <input
                        ref="fileInput"
                        type="file"
                        accept="image/*"
                        @change="onLogoChange"
                        class="hidden-input"
                    />
                </div>
            </div>
            <div class="title-info">
                <h1 class="page-title">{{ stekName }}</h1>
                <div v-if="!isEditing && fqdn" class="fqdn">
                    {{ fqdn }}
                </div>
                <div v-if="isEditing" class="fqdn-edit">
                    <InputText
                        v-model="fqdn"
                        placeholder="Enter domain (e.g., example.com)"
                        aria-label="Fully Qualified Domain Name"
                        :class="{ 'p-invalid': fqdnError }"
                        @input="validateFqdn"
                    />
                    <small v-if="fqdnError" class="p-error">{{
                        fqdnError
                    }}</small>
                </div>
            </div>
        </div>
        <div class="controls">
            <Button
                v-if="stek && canDeleteStek"
                severity="secondary"
                @click="deleteStek"
                :label="`Delete ` + stekName"
                icon="pi pi-trash"
                class="ml-2"
            />
        </div>
    </div>

    <section :class="{ 'edit': isEditing }">
        <DomainsList :isEditing="isEditing" :domains="domains" />
    </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useDomainsManager } from "@/stores/domains-store";
import DomainsList from "@/components/Domains/DomainsList.vue";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { useRouter } from "vue-router";
import { useEditManager } from "@/composables/edit-manager";
import { useStekManager, useStekStore } from "@/composables/stek-manager";
import Spinner from "@/shared/Spinner.vue";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useUserManager } from "@/stores/user-store";
import InputText from "primevue/inputtext";

// Placeholder logo for when logo load fails or is unavailable
const PLACEHOLDER_LOGO =
    "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz48IS0tIFVwbG9hZGVkIHRvOiBTVkcgUmVwbywgd3d3LnN2Z3JlcG8uY29tLCBHZW5lcmF0b3I6IFNWRyBSZXBvIE1peGVyIFRvb2xzIC0tPg0KPHN2ZyB3aWR0aD0iODAwcHgiIGhlaWdodD0iODAwcHgiIHZpZXdCb3g9IjAgMCAxMjAgMTIwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPg0KPHJlY3Qgd2lkdGg9IjEyMCIgaGVpZ2h0PSIxMjAiIGZpbGw9IiNFRkYxRjMiLz4NCjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMzMuMjUwMyAzOC40ODE2QzMzLjI2MDMgMzcuMDQ3MiAzNC40MTk5IDM1Ljg4NjQgMzUuODU0MyAzNS44NzVIODMuMTQ2M0M4NC41ODQ4IDM1Ljg3NSA4NS43NTAzIDM3LjA0MzEgODUuNzUwMyAzOC40ODE2VjgwLjUxODRDODUuNzQwMyA4MS45NTI4IDg0LjU4MDcgODMuMTEzNiA4My4xNDYzIDgzLjEyNUgzNS44NTQzQzM0LjQxNTggODMuMTIzNiAzMy4yNTAzIDgxLjk1NyAzMy4yNTAzIDgwLjUxODRWMzguNDgxNlpNODAuNTAwNiA0MS4xMjUxSDM4LjUwMDZWNzcuODc1MUw2Mi44OTIxIDUzLjQ3ODNDNjMuOTE3MiA1Mi40NTM2IDY1LjU3ODggNTIuNDUzNiA2Ni42MDM5IDUzLjQ3ODNMODAuNTAwNiA2Ny40MDEzVjQxLjEyNTFaTTQzLjc1IDUxLjYyNDlDNDMuNzUgNTQuNTI0NCA0Ni4xMDA1IDU2Ljg3NDkgNDkgNTYuODc0OUM1MS44OTk1IDU2Ljg3NDkgNTQuMjUgNTQuNTI0NCA1NC4yNSA1MS42MjQ5QzU0LjI1IDQ4LjcyNTQgNTEuODk5NSA0Ni4zNzQ5IDQ5IDQ2LjM3NDlDNDYuMTAwNSA0Ni4zNzQ5IDQzLjc1IDQ4LjcyNTQgNDMuNzUgNTEuNjI0OVoiIGZpbGw9IiM2ODc3ODciLz4NCjwvc3ZnPg==";

const toast = useToast();
const { user } = useUserManager();
const { organization } = useOrganizationManager();
const { logo, fqdn, stek, isLoadingSteks } = useStekManager();
const { domains, isFetching } = useDomainsManager();
const { isEditing } = useEditManager();
const router = useRouter();

const fqdnError = ref("");
const logoDataUrl = ref("");
const fileInput = ref<HTMLInputElement | null>(null);
const { remove } = useStekStore();

// Handle logo loading errors
const handleLogoError = () => {
    console.warn("Stek logo failed to load, using placeholder");
    // Set to placeholder logo if the image fails to load
    logoDataUrl.value = PLACEHOLDER_LOGO;
};

const canDeleteStek = computed(() => {
    // FIXME: Actually check if the user has the correct permissions
    const orgId = organization.value?.identity || "";
    return user.value?.roles[orgId] === "user-manager";
});

const validateFqdn = () => {
    const fqdnRegex =
        /^(?!:\/\/)(?:[a-zA-Z0-9-]+\.)*[a-zA-Z0-9][a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/;
    if (!stek.value?.fqdn) {
        fqdnError.value = "";
    } else if (!fqdnRegex.test(stek.value.fqdn)) {
        fqdnError.value = "Please enter a valid domain name";
    } else {
        fqdnError.value = "";
    }
};

const triggerFileInput = () => {
    fileInput.value?.click();
};

const onLogoChange = (event: Event) => {
    if (!logo.value) {
        return;
    }

    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    if (file.size > 2000000) {
        toast.add({
            severity: "error",
            summary: "Upload failed",
            detail: "File must be less than 2MB",
        });
        return;
    }

    const allowedTypes = ["image/png", "image/jpeg", "image/svg+xml"];
    if (!allowedTypes.includes(file.type)) {
        toast.add({
            severity: "error",
            summary: "Invalid file type",
            detail: "Please upload a PNG, JPG, or SVG file",
        });
        return;
    }
    logo.value.updateContent(file);
    input.value = ""; // Reset input for future uploads
};

watch(
    logo,
    async (newLogo) => {
        if (newLogo?.content) {
            logoDataUrl.value = await newLogo.getDataUrl();
        } else {
            // Use placeholder logo
            logoDataUrl.value = PLACEHOLDER_LOGO;
        }
    },
    { immediate: true, deep: true }
);

const stekName = computed(() => stek.value?.name || "...");
const displaySpinner = computed(() => {
    return isFetching.value || isLoadingSteks.value;
});

const confirm = useConfirm();

const deleteStek = async () => {
    if (!stek.value) {
        return;
    }
    let message = `This will remove EVERYTHING inside ${stek.value.name}.`;
    message += `\n\nThis will remove ${domains.value.length} domains and their children.`;
    if (domains.value.length) {
        message +=
            "\n\n" + `Domains: ${domains.value.map((d) => d.name).join(",")}.`;
    }
    confirm.require({
        header: "Are you sure?",
        message: message,
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: "Cancel",
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: "Delete",
            severity: "danger",
        },
        defaultFocus: "reject",
        accept: async () => {
            if (!stek.value || !organization.value) {
                return;
            }
            await remove({ showToast: true });
            router.push({
                name: "stekPage",
                params: {
                    orgName: organization.value.name,
                },
            });
        },
    });
};
</script>

<style scoped lang="scss">
.title-section {
    display: flex;
    align-items: center;
    gap: 1rem;

    .logo-container {
        position: relative;
        height: 64px;
        max-width: 250px;

        .stek-logo {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }

        &.editing {
            cursor: pointer;

            &:hover .logo-edit-overlay {
                opacity: 1;
            }
        }

        .logo-edit-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: rgba(0, 0, 0, 0.5);
            color: white;
            z-index: 1;
            opacity: 0;
            transition: opacity 0.2s;
            border-radius: 4px;

            &:focus-visible {
                opacity: 1;
                outline: 2px solid var(--primary-color);
                outline-offset: 2px;
            }
        }

        .hidden-input {
            display: none;
        }
    }

    .title-info {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;

        .fqdn {
            color: var(--text-color-secondary);
            font-size: 0.9rem;
        }

        .fqdn-edit {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;

            .help-text {
                color: var(--text-color-secondary);
            }
        }
    }
}

.title-and-controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
}

.loading-message {
    color: var(--text-color-secondary);
    font-style: italic;
    padding: 1rem 0;
}
</style>
